import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, ReactiveFormsModule, FormGroup, FormControl } from '@angular/forms';
import { CourierProfileService } from '../../shared/services/courier-profile.service';
import { CourierOptionDefinition } from '../../../models/courier-option-definition';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { delay, distinctUntilChanged, map, take, tap, withLatestFrom } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { AppState } from '../../../store';
import { CourierOptionMenuComponent } from '../courier-option-menu/courier-option-menu.component';
import {
    selectCourierOptions,
    selectReferenceDefinitions,
    selectThirdPartyAccountAllowed,
    selectThirdPartyAccountChoices,
    selectThirdPartyAccountLimited
} from '../../../store/profile/profile.state';
import {
    shipmentEditorAddReference,
    shipmentEditorRemoveCourierOption,
    shipmentEditorRemoveReference, shipmentEditorUpdateOptionsSection
} from '../../../store/shipment-editor/shipment-editor.actions';
import { InteractionEnum } from '../../../enum/interaction.enum';
import { PaymentOptionEnum } from '../../../enum/payment-option.enum';
import { ThirdPartyAccountOption } from '../../../models/third-party-account-option';
import {
    selectShipmentEditorActiveOptionDefinitions, selectShipmentEditorCategory, selectShipmentEditorMetric,
    selectShipmentEditorPaymentOption, selectShipmentEditorReferenceGroups, selectShipmentEditorShipment,
    selectShipmentEditorValid
} from '../../../store/shipment-editor/shipment-editor.state';
import { MatDialog } from '@angular/material/dialog';
import { SignatureOptionEnum } from '../../../enum/signature-option.enum';
import { ShipmentReferenceEditorComponent } from '../shipment-reference-editor/shipment-reference-editor.component';
import { MatDivider } from '@angular/material/divider';
import { MatIconButton, MatButton } from '@angular/material/button';
import { CourierOptionEditorComponent } from '../courier-option-editor/courier-option-editor.component';
import { IconComponent } from '../../shared/icon/icon.component';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { AsyncPipe } from '@angular/common';
import { MatDatepickerInput, MatDatepickerToggle, MatDatepicker } from '@angular/material/datepicker';
import { MatInput } from '@angular/material/input';
import { MatFormField, MatLabel, MatSuffix, MatError } from '@angular/material/form-field';
import { Shipment } from '../../../models/shipment';
import { emptyShipment } from '../../../constants/empty-shipment';
import { ReferencesModification } from '../../../models/references-modification';
import { DateService } from '../../shared/services/date.service';
import { ShipmentCategoryEnum } from '../../../enum/shipment-category.enum';

@Component({
    selector: 'app-shipment-options',
    templateUrl: './shipment-options.component.html',
    styleUrls: [ './shipment-options.component.scss' ],
    imports: [ReactiveFormsModule, MatFormField, MatLabel, MatInput, MatDatepickerInput, MatDatepickerToggle, MatSuffix, MatDatepicker, MatError, MatSelect, MatOption, IconComponent, CourierOptionEditorComponent, MatIconButton, MatDivider, MatButton, ShipmentReferenceEditorComponent, AsyncPipe]
})
export class ShipmentOptionsComponent implements OnInit {

    disabled = false;

    today = new Date();

    formGroup: FormGroup;
    stateSubscription: Subscription;
    formSubscription: Subscription;

    @Input()
    courierOptionDefinitions: CourierOptionDefinition[] = [];

    @Input()
    referenceDefinitions: ReferencesModification = { grouping: [], min_count: 1, max_count: 3 };

    protected readonly signatureOptionEnum = SignatureOptionEnum;

    constructor(private store: Store<AppState>, private courierProfileService: CourierProfileService, private dialog: MatDialog) {

    }

    canDeleteReference = false;
    canAddReference = false;
    paymentOptionEnum = PaymentOptionEnum;
    thirdPartyAccountAllowed$ = combineLatest([
        this.store.select(selectThirdPartyAccountAllowed),
        this.store.select(selectShipmentEditorCategory)
    ]).pipe(
        map(([ allowed, category ]) => allowed && category === ShipmentCategoryEnum.PARCEL)
    );
    thirdPartyAccountLimited$ = this.store.select(selectThirdPartyAccountLimited);
    thirdPartyAccountChoices$ = this.store.select(selectThirdPartyAccountChoices);
    thirdPartyAccountActive$ = this.store.select(selectShipmentEditorPaymentOption).pipe(
        withLatestFrom(this.thirdPartyAccountAllowed$),
        map(([ option, allowed ]) => allowed && option === PaymentOptionEnum.THIRD_PARTY)
    );
    thirdPartyAccountSelector$ = this.thirdPartyAccountChoices$.pipe(
        map(choices => choices.length > 0)
    );
    selectedOptions$ = this.store.select(selectShipmentEditorActiveOptionDefinitions).pipe(
        tap(options => console.log(options))
    );
    activeReferenceGroups$ = this.store.select(selectShipmentEditorReferenceGroups);
    displayReferenceGroups$ = this.activeReferenceGroups$.pipe(
        delay(0),
        withLatestFrom(this.store.select(selectReferenceDefinitions)),
        tap(([ groups, references ]) => this.canDeleteReference = groups.length > (references?.min_count || 1)),
        tap(([ groups, references ]) => this.canAddReference = groups.length < (references?.max_count || 1)),
        map(([ groups, ]) => groups)
    );
    metric$ = this.store.select(selectShipmentEditorMetric);
    displaySignatureOption$ = this.store.select(selectShipmentEditorCategory).pipe(
        map(category => category === ShipmentCategoryEnum.PARCEL)
    );
    displayShipmentDate$ = this.store.select(selectShipmentEditorCategory).pipe(
        map(category => category === ShipmentCategoryEnum.PARCEL)
    );

    ngOnInit(): void {
        this.formGroup = this.createFormGroup(emptyShipment);
        this.stateSubscription = this.store.select(selectShipmentEditorShipment).subscribe((shipment) => {
            this.updateOptionsShape(shipment);
            this.formGroup.patchValue({
                shipmentDate: shipment.shipmentDate,
                paymentOption: shipment.paymentOption,
                paymentAccountNumber: shipment.paymentAccountNumber,
                signatureOption: shipment.courierOptions.signature_required,
                courierOptions: shipment.courierOptions
            }, { emitEvent: false });
        });
        this.formSubscription = this.formGroup.valueChanges.subscribe(value => {
            const shipmentDate = value.shipmentDate instanceof Date ? DateService.isoDate(value.shipmentDate) : value.shipmentDate;
            this.store.dispatch(shipmentEditorUpdateOptionsSection({
                shipmentDate,
                paymentOption: value.paymentOption,
                paymentAccountNumber: value.paymentAccountNumber,
                signatureOption: value.signatureOption
            }));
        });
    }

    addOption(): void {
        this.dialog.open(CourierOptionMenuComponent, {
            width: '520px',
            height: '400px'
        });
    }

    removeOption(option: CourierOptionDefinition): void {
        this.store.dispatch(shipmentEditorRemoveCourierOption({ option }));
    }

    addReference(): void {
        this.store.dispatch(shipmentEditorAddReference());
    }

    removeReference(index: string): void {
        this.store.dispatch(shipmentEditorRemoveReference({ index: parseInt(index, 10) }));
    }

    userChange(): void {
        // this.updated.emit(InteractionEnum.DEFAULT);
    }

    updateOptionsShape(shipment: Shipment): void {
        const optionsGroup = this.formGroup.get('courierOptions') as FormGroup;
        this.courierOptionDefinitions.forEach(o => {
            if (optionsGroup.get(o.code) === null) {
                optionsGroup.addControl(o.code, new FormControl(shipment.courierOptions[o.code] ? '1' : '0'), { emitEvent: false });
                o.parameters.forEach(p => {
                    if (optionsGroup.get(p.code) === null) {
                        optionsGroup.addControl(p.code, new FormControl(shipment.courierOptions[p.code] ?? ''), { emitEvent: false });
                    }
                });
            }
        });
    }

    createOptionsGroup(shipment: Shipment): FormGroup {
        const group = {};
        this.courierOptionDefinitions.forEach(o => {
            group[o.code] = new FormControl(shipment.courierOptions[o.code] ? '1' : '0');
            o.parameters.forEach(p => {
                group[p.code] = new FormControl(shipment.courierOptions[p.code] ?? '');
            });
        });
        return new FormGroup(group);
    }

    createFormGroup(shipment: Shipment): FormGroup {
        return new FormGroup({
            shipmentDate: new FormControl(shipment.shipmentDate),
            paymentOption: new FormControl(shipment.paymentOption),
            paymentAccountNumber: new FormControl(shipment.paymentAccountNumber),
            signatureOption: new FormControl(shipment.signatureOption),
            courierOptions: this.createOptionsGroup(shipment)
        });
    }
}
